import type { Variety } from "@/lib/catalog/types";
import type { Card } from "@/lib/catalog/grouping";
import { categoryRoutePath } from "@/lib/catalog/category-route";
import { MAGNAFLOR_PHONE_SCHEMA } from "@/lib/contact-info";
import {
  CATEGORY_LABEL_I18N,
  COLOR_I18N,
} from "@/lib/catalog/variety-i18n-labels";

// Step 8.5 Half B commit 15: structured-data builders.
//
// Locked decision Q3: NO prices in public JSON-LD. Wholesale pricing
// is sign-in gated, so the Product offer is an AggregateOffer carrying
// availability + currency + a URL that leads to sign-in - never
// lowPrice/highPrice/price (Mayesh approach).
//
// Geographic lock (Jun 9): areaServed is the United States. Miami is
// the HQ address, never an exclusive service-area descriptor.

// D-038 Wave 5a follow-up (PASTE #177, WA #45): resolve from
// NEXT_PUBLIC_SITE_ORIGIN at runtime so JSON-LD URLs (Organization,
// WebPage, BreadcrumbList, OG, ImageObject license/credit) match the
// canonical the rest of the portal emits via app/layout.tsx
// metadataBase.  Same fallback chain as that file — current LIVE URL
// per Jun 23 watchlist; § 3 single-domain magnaflor.com canonical
// post-cutover.  One env-var flip at Step 10.5+ DNS cutover.
export const SITE_ORIGIN =
  process.env.NEXT_PUBLIC_SITE_ORIGIN ??
  // PASTE #261: post-cutover canonical. Prod sets NEXT_PUBLIC_SITE_ORIGIN;
  // this fallback only guards an unset env, kept on the live apex.
  "https://magnaflor.com";

const CATEGORY_LABEL: Record<Variety["category"], string> = {
  rose: "Roses",
  "garden-spray-rose": "Garden & Spray Roses",
  hydrangea: "Hydrangeas",
  specialty: "Specialty Flowers",
  "filler-green": "Fillers & Greens",
};

export function buildOrganizationLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Magnaflor",
    // PASTE #209 Wave 1b-i: complete the Organization entity so search
    // engines + AI assistants treat Magnaflor as an established, trusted
    // brand. Family framing only — no named founder Person.
    legalName: "Magnaflor Corp",
    foundingDate: "2016",
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/og/brand`,
    // DIRECTIVE-040 commit 5: describe the Florida direct + Doral
    // pickup scope so AI search assistants surface the geography
    // accurately. NO dollar amounts here per Q3 lock — the
    // marketing copy + /about/ordering page carry the prices.
    description:
      "Farm-direct wholesale cut flowers from Ecuador and Colombia for florists across the United States. Direct flat-fee delivery throughout Florida; order-ahead pickup in Doral (Miami) at our logistics partner's facility for partners outside Florida. A family-owned business run by the Erazo family, farm-direct from Ecuador since 2016.",
    telephone: MAGNAFLOR_PHONE_SCHEMA,
    email: "sales@magnaflor.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "777 Brickell Avenue, Suite 500",
      addressLocality: "Miami",
      addressRegion: "FL",
      postalCode: "33131",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: MAGNAFLOR_PHONE_SCHEMA,
      email: "sales@magnaflor.com",
      contactType: "sales",
      areaServed: "US",
      availableLanguage: ["en", "es"],
    },
    areaServed: [
      {
        "@type": "State",
        name: "Florida",
        containedInPlace: { "@type": "Country", name: "United States" },
      },
      {
        "@type": "Country",
        name: "United States",
      },
    ],
    sameAs: [
      "https://www.instagram.com/magnaflorflowers/",
      "https://www.facebook.com/magnaflorcompany/",
      "https://www.linkedin.com/company/magnaflor1",
      "https://www.pinterest.com/magnaflor/",
      "https://www.youtube.com/@magnaflor7845",
      "https://www.tiktok.com/@magnaflorcompany",
      "https://x.com/magnaflorusa",
    ],
  };
}

// PASTE #227 Wave 2a: standalone WebSite node with a SearchAction so
// Google can offer a sitelinks search box that queries the catalog.
// Emitted sitewide from the root layout alongside the Organization node.
export function buildWebSiteLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Magnaflor",
    url: SITE_ORIGIN,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_ORIGIN}/catalog?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

// Step 8.5 Half B commit 18: WebPage schema for the /about pages.
export function buildWebPageLd(
  title: string,
  description: string,
  path: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: `${SITE_ORIGIN}${path}`,
    isPartOf: {
      "@type": "WebSite",
      name: "Magnaflor",
      url: SITE_ORIGIN,
    },
    publisher: { "@type": "Organization", name: "Magnaflor" },
  };
}

// MKT-PASTE #8: Service schema for the metro landing pages. areaServed is a
// list of City nodes (Tampa / St. Petersburg / Clearwater, ...). CRITICAL:
// NO address and NO LocalBusiness - we have no physical location in these
// metros (same rule that protects us on Doral). The provider is the
// Organization; the metro page is a service-area page, not a storefront.
export function buildServiceLd(input: {
  name: string;
  description: string;
  path: string;
  areaServed: ReadonlyArray<string>;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Wholesale flower supply",
    name: input.name,
    description: input.description,
    url: `${SITE_ORIGIN}${input.path}`,
    provider: {
      "@type": "Organization",
      name: "Magnaflor",
      url: SITE_ORIGIN,
    },
    areaServed: input.areaServed.map((a) => ({ "@type": "City", name: a })),
  };
}

// Shared BreadcrumbList builder (single source of truth): turns an
// ordered list of {name, item} crumbs into a valid schema.org
// BreadcrumbList with sequential positions. Used by the variety-page
// breadcrumb AND the category-hub breadcrumb so the shape never diverges.
function breadcrumbListLd(
  crumbs: ReadonlyArray<{ name: string; item: string }>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  };
}

// MKT-PASTE #1 Part B: generic breadcrumb builder for standalone marketing
// pages (e.g. the Ecuador cornerstone) that are not variety or category
// pages. Thin exported wrapper over the shared builder so the shape never
// diverges from the catalog breadcrumbs.
export function buildGenericBreadcrumbLd(
  crumbs: ReadonlyArray<{ name: string; item: string }>,
): Record<string, unknown> {
  return breadcrumbListLd(crumbs);
}

export function buildBreadcrumbLd(
  card: Card,
  variety: Variety,
): Record<string, unknown> {
  return breadcrumbListLd([
    { name: "Home", item: SITE_ORIGIN },
    { name: "Catalog", item: `${SITE_ORIGIN}/catalog` },
    {
      name: CATEGORY_LABEL[variety.category],
      // PASTE #267: point the breadcrumb at the dedicated, indexable
      // category page (/catalog/<plural>), not the weak legacy
      // category-filter query URL (which canonicals to /catalog).
      item: `${SITE_ORIGIN}${categoryRoutePath(variety.category)}`,
    },
    { name: card.name, item: `${SITE_ORIGIN}/catalog/${card.routeSlug}` },
  ]);
}

// PASTE #271 (audit W3): 3-level breadcrumb for the dedicated category hub
// pages (Home > Catalog > {Category}). Built via the shared breadcrumbListLd
// builder - single source of truth with the variety-page breadcrumb.
//
// Locale-aware so the JSON-LD MATCHES the page's rendered visible breadcrumb
// (Google's guideline): on /es the hub shows "Inicio > Catálogo > Rosas" with
// /es item URLs, so the schema mirrors that (self-canonical to the /es hub).
// The caller passes the already-localized category label (CategoryLandingPage
// owns the en/es label map) to avoid duplicating it here.
export function buildCategoryBreadcrumbLd(
  category: Variety["category"],
  locale: "en" | "es",
  categoryLabel: string,
): Record<string, unknown> {
  const base = locale === "es" ? `${SITE_ORIGIN}/es` : SITE_ORIGIN;
  return breadcrumbListLd([
    { name: locale === "es" ? "Inicio" : "Home", item: base },
    {
      name: locale === "es" ? "Catálogo" : "Catalog",
      item: `${base}/catalog`,
    },
    { name: categoryLabel, item: `${base}${categoryRoutePath(category)}` },
  ]);
}

// Brand canon (Jun 9, 2026; #446 de-brag Aug 28): the internal rose brand's
// legal name never ships in public output. The public origin value is a
// resolvable PLACE that carries the brand - no ownership possessive. The DDB
// fetch layer sanitizes farm.name at the boundary, so client components may
// see either form - isOwnProduction accepts both.
export const OWN_PRODUCTION_PUBLIC_NAME =
  "Magnaflor branded roses — Cayambe, Ecuador, 3,200 m";

// MKT-#117/#125/#127: the partner network's internal key ("Partner Farm *
// Ecuador") is a placeholder - the partner varieties actually source from BOTH
// Ecuador and Colombia (Alexis, Aug 28: "Ecuador and Colombia is true"). One
// farm record with an "Ecuador" name would assert a single, false country in
// structured data for the Colombian stems, so the public value is the true
// two-country PLACE that matches the visible copy + alt text. MKT-#126 will
// make the country list DATA per variety; until then this is the honest floor.
export const PARTNER_ORIGIN_PUBLIC_PLACE =
  "Ecuadorian and Colombian highlands";

// MKT-#144: the catalog rows were migrated OFF the internal sentinels, so
// "Flower Motion" and "Partner Farm *" no longer appear anywhere - bundle or
// payload. The strings layer STAYS unchanged: it maps the stored rose value to
// the branded, altitude-bearing visible/schema value (OWN_PRODUCTION_PUBLIC_NAME
// keeps "branded" - item 1 was withdrawn), and the ES page composes its own
// approved Origin copy. The rose match key is now the migrated stored value;
// partner stores its public place directly, so publicFarmName returns it as-is.
const ROSE_STORED_FARM_NAME = "Magnaflor roses — Cayambe, Ecuador";

export function publicFarmName(farmName: string): string {
  if (farmName === ROSE_STORED_FARM_NAME) return OWN_PRODUCTION_PUBLIC_NAME;
  return farmName;
}

export function isOwnProduction(farmName: string): boolean {
  return (
    farmName === ROSE_STORED_FARM_NAME ||
    farmName === OWN_PRODUCTION_PUBLIC_NAME
  );
}

// Competitive superiority brief (WA #24, locked Jun 9): every variety
// ships at least 6 PropertyValue entries - competitors stop at basic
// Product schema. Color / purchase format / box formats are always
// derivable, so the floor holds even for spec-light varieties.
const BOX_LABEL: Record<"EB" | "QB" | "HB", string> = {
  EB: "Eighth Box",
  QB: "Quarter Box",
  HB: "Half Box",
};

function colorLabel(tag: string): string {
  return tag
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function boxFormats(variety: Variety): string {
  const codes = new Set<"EB" | "QB" | "HB">();
  for (const cap of Object.values(variety.boxSizes)) {
    for (const code of ["EB", "QB", "HB"] as const) {
      if (cap[code] !== null && cap[code] !== undefined) codes.add(code);
    }
  }
  return (["EB", "QB", "HB"] as const)
    .filter((c) => codes.has(c))
    .map((c) => BOX_LABEL[c])
    .join(", ");
}

export type ProductLdExtras = {
  breeder?: string;
  vaseLife?: string;
};

function additionalProperties(
  variety: Variety,
  extras?: ProductLdExtras,
): Array<Record<string, unknown>> {
  const props: Array<Record<string, unknown>> = [
    {
      "@type": "PropertyValue",
      name: "Farm",
      value: publicFarmName(variety.farm.name),
    },
    {
      "@type": "PropertyValue",
      name: "Origin",
      value: `${variety.farm.region}, ${variety.farm.country}`,
    },
    {
      "@type": "PropertyValue",
      name: "Altitude",
      value: `${variety.farm.altitudeMeters}m`,
    },
    {
      "@type": "PropertyValue",
      name: "Color",
      value: colorLabel(variety.colorTag),
    },
    {
      "@type": "PropertyValue",
      name: "Sold by",
      value: variety.purchaseUnit === "box" ? "Box" : "Bunch",
    },
  ];
  const formats = boxFormats(variety);
  if (formats) {
    props.push({
      "@type": "PropertyValue",
      name: "Box formats",
      value: formats,
    });
  }
  if (extras?.breeder) {
    props.push({
      "@type": "PropertyValue",
      name: "Breeder",
      value: extras.breeder,
    });
  }
  if (extras?.vaseLife) {
    props.push({
      "@type": "PropertyValue",
      name: "Vase life",
      value: extras.vaseLife,
    });
  }
  if (variety.grade !== undefined) {
    props.push({
      "@type": "PropertyValue",
      name: "Grade",
      value: variety.grade,
    });
  }
  if (variety.headSizeCm !== undefined) {
    props.push({
      "@type": "PropertyValue",
      name: "Head size",
      value: `${variety.headSizeCm} cm`,
    });
  }
  if (variety.petalCount !== undefined) {
    props.push({
      "@type": "PropertyValue",
      name: "Petal count",
      value: String(variety.petalCount),
    });
  }
  if (variety.lengths.length > 0) {
    props.push({
      "@type": "PropertyValue",
      name: "Stem lengths",
      value: `${variety.lengths.join(", ")} cm`,
    });
  }
  if (variety.stemsPerBunch !== null) {
    props.push({
      "@type": "PropertyValue",
      name: "Stems per bunch",
      value: String(variety.stemsPerBunch),
    });
  }
  return props;
}

// MKT-#54: the no-price AggregateOffer stub (PASTE #268) was REMOVED. It carried
// priceCurrency + availability + a /sign-in url but no lowPrice, which Google
// rejects outright ("missing field lowPrice in offers") - it invalidated all 161
// public Products. A Product with no `offers` is valid schema. Prices are never
// published (locked), and we deliberately do not add review/aggregateRating.

export function buildProductLd(
  card: Card,
  variety: Variety,
  description?: string,
  extras?: ProductLdExtras,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: card.name,
    image:
      variety.photoUrls?.primary?.og ??
      `${SITE_ORIGIN}/og/variety/${card.routeSlug}`,
    description:
      description ??
      `${card.name} - ${variety.colorTag} ${CATEGORY_LABEL[variety.category]}, farm-direct from ${publicFarmName(variety.farm.name)}, ${variety.farm.country}. Wholesale for US florists; pricing shown after sign-in.`,
    brand: { "@type": "Brand", name: "Magnaflor" },
    category: `Wholesale Cut Flowers > ${CATEGORY_LABEL[variety.category]}`,
    color: variety.colorTag,
    additionalProperty: additionalProperties(variety, extras),
    // MKT-#54: NO `offers` block. An AggregateOffer without lowPrice is rejected
    // by Google ("missing field lowPrice") - it invalidated all 161 Products. A
    // Product with no offers is VALID schema (valid-but-not-rich). Prices are
    // never published (locked), and we deliberately do NOT add review /
    // aggregateRating (we have none). See buildCollectionPageLd for the twin.
  };
}

// Step 10.5 commit 8: ImageObject JSON-LD per
// MEDIA_SEO_BEST_PRACTICES § 1.7. Embedded in the variety detail page
// alongside Product schema so search engines + LLM crawlers see
// authored image metadata (caption, credit, license, optional geo for
// Cayambe farm in Ecuador shots) for every significant photo.
//
// caption format mirrors § 1.1 alt-text spec: "{variety} {color}
// {category}, {head size if known}, grown at {origin}". Cayambe, Ecuador geo
// is added only for own-production photos; commit 2 MEDIA AUDIT
// product shots are studio-shot and intentionally carry no geo.

const CAYAMBE_GEO = {
  "@type": "GeoCoordinates",
  latitude: -0.0306,
  longitude: -78.1411,
  elevation: 3200,
};
const LICENSE_URL = `${SITE_ORIGIN}/license`;

export function buildVarietyImageObjectLd(
  variety: Variety,
  contentUrl: string,
  options: {
    width?: number;
    height?: number;
    isOwnProduction: boolean;
    // MKT-#117: default "en" keeps the exact EN caption today. "es" builds the
    // Spanish caption so ES variety pages stop emitting an English ImageObject.
    locale?: "en" | "es";
  },
): Record<string, unknown> {
  const es = options.locale === "es";
  const captionParts: string[] = [];
  captionParts.push(variety.name);
  if (es) {
    // ES grammar: noun then adjective ("rosa roja"). Resolves the same maps as
    // the hero alt text so both Spanish surfaces read identically.
    const catEs = CATEGORY_LABEL_I18N[variety.category].es;
    const colorEs = COLOR_I18N[variety.colorTag]?.es ?? variety.colorTag;
    captionParts.push(`${catEs} ${colorEs}`);
  } else {
    captionParts.push(`${variety.colorTag} ${CATEGORY_LABEL[variety.category]}`);
  }
  if (variety.headSizeCm !== undefined) {
    captionParts.push(
      es
        ? `cabeza de ${String(variety.headSizeCm).replace(".", ",")} cm`
        : `${variety.headSizeCm} cm head`,
    );
  }
  if (variety.petalCount !== undefined) {
    captionParts.push(
      es ? `${variety.petalCount} pétalos` : `${variety.petalCount} petals`,
    );
  }
  // #446b + MKT-#117/#125/#127: the caption is a SENTENCE, so it carries a
  // place phrase, NOT the branded-label constant (which reads correctly only as
  // a Farm: label:value). Own-production takes the Cayambe altitude line;
  // partner varieties take the TRUE two-country origin - never a single country
  // (that would be false for the Colombian stems). Mirrors the hero alt text so
  // the caption and alt agree in each language.
  let origin: string;
  if (options.isOwnProduction) {
    origin = es
      ? `cultivada a 3.200 m en Cayambe, Ecuador`
      : `grown at 3,200 m in Cayambe, Ecuador`;
  } else {
    origin = es
      ? `cultivada en las tierras altas ecuatorianas y colombianas`
      : `grown in the Ecuadorian and Colombian highlands`;
  }
  captionParts.push(origin);
  const caption = captionParts.join(", ");

  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl,
    caption,
    creditText: "Magnaflor",
    creator: { "@type": "Organization", name: "Magnaflor" },
    copyrightNotice: `© ${new Date().getFullYear()} Magnaflor`,
    license: LICENSE_URL,
    acquireLicensePage: LICENSE_URL,
  };
  if (options.width) ld.width = options.width;
  if (options.height) ld.height = options.height;
  if (options.isOwnProduction) {
    ld.contentLocation = {
      "@type": "Place",
      name: "Cayambe, Ecuador",
      geo: CAYAMBE_GEO,
    };
  }
  return ld;
}

// D-038 Wave 5a amend (PASTE #170, WA #45 § 1.7): ImageObject JSON-LD
// for marketing-page heroes + feature photos.  Distinct from
// buildVarietyImageObjectLd above because the caption / creator /
// optional Cayambe, Ecuador geo are derived from the asset slug + alt text
// rather than from a Variety record.  Cayambe geo only on Ecuador
// production photos; Florida-scope assets (ordering / packing) carry
// no geo per § 1.7.
export function buildMarketingImageObjectLd(input: {
  contentUrl: string;
  caption: string;
  width: number;
  height: number;
  includeCayambeGeo: boolean;
}): Record<string, unknown> {
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: input.contentUrl,
    caption: input.caption,
    creditText: "Magnaflor",
    creator: { "@type": "Organization", name: "Magnaflor" },
    copyrightNotice: `© ${new Date().getFullYear()} Magnaflor - All rights reserved`,
    license: LICENSE_URL,
    acquireLicensePage: LICENSE_URL,
    width: input.width,
    height: input.height,
  };
  if (input.includeCayambeGeo) {
    ld.contentLocation = {
      "@type": "Place",
      name: "Cayambe, Ecuador",
      geo: CAYAMBE_GEO,
    };
  }
  return ld;
}

// Step 10.5 commit 12 / MEDIA_SEO_BEST_PRACTICES § 2.1: VideoObject
// JSON-LD. Emitted on the homepage for the Cayambe, Ecuador hero loop (and on
// /sign-in for the brand-panel loop when it lands). Inline so search
// engines + LLM crawlers see the video metadata without parsing
// HTML5 video elements.

export function buildHeroVideoLd({
  contentUrl,
  thumbnailUrl,
  duration,
  inLanguage = ["en", "es"],
  name = "Magnaflor — Premium Wholesale Roses from Cayambe, Ecuador",
  description = "Cinematic atmospheric film showcasing Magnaflor Cayambe, Ecuador rose production at 3,200m altitude — the origin behind florist-grade premium roses, shipped farm-direct nationwide.",
}: {
  contentUrl: string;
  thumbnailUrl: string;
  // ISO 8601 duration (e.g., "PT30S").
  duration: string;
  inLanguage?: string[];
  // PASTE #183 BATCH A: optional override so the same helper can emit
  // a VideoObject for the sign-in brand-panel reuse of the Cayambe, Ecuador
  // loop with surface-appropriate name + description (vertical
  // crop, sign-in context) without duplicating the publisher / geo /
  // license boilerplate.
  name?: string;
  description?: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name,
    description,
    thumbnailUrl,
    uploadDate: "2026-06-15T00:00:00+00:00",
    duration,
    contentUrl,
    publisher: {
      "@type": "Organization",
      name: "Magnaflor",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/og/brand`,
      },
    },
    contentLocation: {
      "@type": "Place",
      name: "Cayambe, Ecuador",
      geo: CAYAMBE_GEO,
    },
    inLanguage,
  };
}

// Step 8.5 Half B commit 22: CollectionPage schema for the catalog
// listing. Price-free per locked Q3 - the ItemList carries only names
// and urls, never offers.
//
// PASTE #183 BATCH B: each ListItem now wraps a Product with image
// (and category) when the caller passes per-card photoUrl + category.
// Closes the 76-RED imageObjectOk audit gap on /catalog without
// emitting N separate ImageObject blocks (Schema.org's Product.image
// field satisfies image-of-the-listed-entity for catalog index).
// Price-free remains locked - Product carries no offers / price.
export type CatalogCardLdInput = {
  name: string;
  routeSlug: string;
  imageUrl?: string;
  category?: Variety["category"];
};

export function buildCollectionPageLd(
  cards: CatalogCardLdInput[],
  category?: Variety["category"],
): Record<string, unknown> {
  // PASTE #267: a category-scoped CollectionPage points at the dedicated
  // /catalog/<plural> landing page (self-canonical on that page; a funnel
  // target from the legacy category-filter view, which canonicals to /catalog).
  const url = category
    ? `${SITE_ORIGIN}${categoryRoutePath(category)}`
    : `${SITE_ORIGIN}/catalog`;
  const name = category
    ? `Wholesale ${CATEGORY_LABEL[category]} - Magnaflor`
    : "Wholesale Flower Catalog - Magnaflor";
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    url,
    isPartOf: {
      "@type": "WebSite",
      name: "Magnaflor",
      url: SITE_ORIGIN,
    },
    publisher: { "@type": "Organization", name: "Magnaflor" },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: cards.length,
      itemListElement: cards.map((c, i) => {
        const itemUrl = `${SITE_ORIGIN}/catalog/${c.routeSlug}`;
        if (c.imageUrl) {
          return {
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: c.name,
              url: itemUrl,
              image: c.imageUrl,
              ...(c.category
                ? { category: CATEGORY_LABEL[c.category] }
                : {}),
              brand: { "@type": "Brand", name: "Magnaflor" },
              // MKT-#54: NO `offers` block - see buildProductLd. The price-less
              // AggregateOffer stub was rejected by Google ("missing lowPrice"),
              // invalidating every list-item Product; a Product with no offers is
              // valid schema. No price ever ships (locked); no review/rating.
            },
          };
        }
        // Backward-compatible: callers that don't pass imageUrl get
        // the original flat ListItem shape.
        return {
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          url: itemUrl,
        };
      }),
    },
  };
}

// Step 8.5 Half B commit 22: FAQPage schema for the /about pages.
// Google requires Q&A pairs to mirror content visible on the page, so
// each page passes items drawn from its rendered prose.
export type FaqItem = { question: string; answer: string };

export function buildFaqPageLd(items: FaqItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

// PASTE #229 Wave 3: Article schema for the florist sourcing guide.
// No price fields (Q3 lock) - the guide's "how to price" section is
// prose ratios only, never dollar amounts in schema.
export function buildArticleLd(input: {
  headline: string;
  description: string;
  path: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    url: `${SITE_ORIGIN}${input.path}`,
    author: { "@type": "Organization", name: "Magnaflor" },
    publisher: {
      "@type": "Organization",
      name: "Magnaflor",
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/icons/logo.png` },
    },
    isPartOf: { "@type": "WebSite", name: "Magnaflor", url: SITE_ORIGIN },
  };
}

// PASTE #229 Wave 3: HowTo schema for step sequences (stem-to-box
// calculator + guide box-math). Steps are plain-text, no price fields.
export type HowToStep = { name: string; text: string };

export function buildHowToLd(input: {
  name: string;
  description: string;
  path: string;
  steps: HowToStep[];
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    url: `${SITE_ORIGIN}${input.path}`,
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}
